// The room above a docked control (the number pad): on a phone the slip scrolls within the space between the stem and
// the pad, so its end is always reachable and the pad covers nothing on first paint. The sheet bounds the room with two
// measurements set here as variables on the format wrapper: the pad's rendered height (--pad-h) and the room's top in
// the page (--room-top), kept current as the pad folds its keys away, the page resizes or a web font arrives.
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const useIso = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const PAD_HEIGHT_VAR = '--pad-h';
export const ROOM_TOP_VAR = '--room-top';

/** The element's top in the page in layout terms: offsets up the chain, so a transform animating an ancestor does not count. */
function pageTop(el: HTMLElement): number {
  let top = 0;
  for (let e: HTMLElement | null = el; e; e = e.offsetParent as HTMLElement | null) top += e.offsetTop;
  return top;
}

/** Writes the two measurements onto the wrapper, or clears them while the control has no size (nothing laid out yet). */
export function measureDock(wrapper: HTMLElement, room: HTMLElement, dock: HTMLElement): void {
  const h = dock.getBoundingClientRect().height;
  if (!(h > 0)) {
    wrapper.style.removeProperty(PAD_HEIGHT_VAR);
    wrapper.style.removeProperty(ROOM_TOP_VAR);
    return;
  }
  wrapper.style.setProperty(PAD_HEIGHT_VAR, `${Math.round(h)}px`);
  wrapper.style.setProperty(ROOM_TOP_VAR, `${Math.round(pageTop(room))}px`);
}

/** Refs for the wrapper, the room and the docked control; measures while `enabled` and clears the variables otherwise. */
export function useDockRoom(enabled: boolean): {
  wrap: RefObject<HTMLDivElement | null>; room: RefObject<HTMLDivElement | null>; dock: RefObject<HTMLElement | null>;
} {
  const wrap = useRef<HTMLDivElement>(null);
  const room = useRef<HTMLDivElement>(null);
  const dock = useRef<HTMLElement>(null);
  useIso(() => {
    const w = wrap.current, r = room.current, d = dock.current;
    if (!enabled || !w || !r || !d) {
      w?.style.removeProperty(PAD_HEIGHT_VAR);
      w?.style.removeProperty(ROOM_TOP_VAR);
      return;
    }
    const run = () => measureDock(w, r, d);
    run();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(run) : undefined;
    ro?.observe(d);
    ro?.observe(w);
    // a banner above the format (offline, not sent) moves the room down without resizing it
    if (typeof document !== 'undefined') ro?.observe(document.body);
    window.addEventListener('resize', run);
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(run, () => undefined);
    return () => { ro?.disconnect(); window.removeEventListener('resize', run); };
  }, [enabled]);
  return { wrap, room, dock };
}
